/**
 * TEAM-HUB-02 — the Team tab, reshaped (Seth 2026-08-20: "Sending messages
 * to the techs should be the primary tool of this tab… we all just need an
 * extremely fast way of seeing the week").
 *
 * · Messages — FIRST and default. Announcements, "please contact this
 *   customer" (carries the customer's name/address/phone), and sales
 *   flashes (new financing/incentives, optional library-doc attachment,
 *   optional expiry). Delivery + seen receipts light up with the field app.
 * · Install week — the weekly install PowerPoint, live. One screen: days
 *   across, installer lanes down, open days obvious at a glance — so
 *   anyone (office, or a selling tech at the kitchen table) can pick an
 *   install date that works for all parties. Reads the ESC mirror's
 *   INSTAL legs (equip list rides in the dispatch notes) + OFF markers.
 * · Scoreboard — wins, plans sold, reviews. Cast it to the break-room TV.
 * · Library — versioned company documents; everyone opens the CURRENT one.
 */
import { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useNavigateWithBack } from "@/lib/navBack";
import {
  CalendarRange, FolderOpen, Megaphone, Pencil,
  Loader2, Send, CheckCheck, Trophy, PhoneCall,
  MessageSquare, ChevronLeft, ChevronRight, Paperclip, X, UserPlus2,
  Contact, Check, Palmtree, Moon,
} from "lucide-react";
import { TechAvatar } from "@/components/TechAvatar";
import { InfoTip } from "@/components/InfoTip";
import { AVAIL_KIND_LABEL, type AvailKind } from "@/features/board/availability";
import { ScoreboardPane } from "./ScoreboardView";
import { RosterPane } from "./RosterPane";
import { OncallPane } from "./OncallPane";
import { LibraryExplorer } from "./LibraryExplorer";
import { useOncallDaysPublished, useOncallSwapRequests, myOncallDaysIn, fmtWeekday as fmtOncallDay, isoDate as oncallISO } from "./oncall";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { PageHeader } from "@/components/ui/PageHeader";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useAllActiveTechs } from "@/features/board/api";
import { CustomerPicker, type PickedCustomer } from "@/components/CustomerPicker";
import {
  useInstallWeek, useSetDayOverride, useAddInstallGuest, useRemoveInstallGuest,
  useLibraryDocs, libraryDocUrl, useBulletins,
  useSendBulletin, LIBRARY_FOLDERS,
  useTimeOffRequests, useDecideTimeOff,
  type InstallLeg, type DayOverride, type LibraryDoc, type MessageKind,
  type Bulletin, type TimeOffRequest,
} from "./team";

type Pane = "messages" | "week" | "oncall" | "scoreboard" | "roster" | "library";
const PANES: Pane[] = ["messages", "week", "oncall", "scoreboard", "roster", "library"];

export function TeamView() {
  // Pane lives in the URL (?pane=week) so drilling into a job and coming
  // back — smart Back or the browser button — lands on the SAME pane
  // (Seth 2026-08-20: back "takes me to the messages bubble, not even the
  // bubble I was on").
  const [searchParams, setSearchParams] = useSearchParams();
  const paneParam = searchParams.get("pane") as Pane | null;
  const pane: Pane = paneParam && PANES.includes(paneParam) ? paneParam : "messages";
  const setPane = (p: Pane) => {
    const next = new URLSearchParams(searchParams);
    next.set("pane", p);
    setSearchParams(next, { replace: true });
  };
  // TIMEOFF-01: waiting requests badge the moment you land on the tab.
  const canApproveTimeOff = useCan("team.approve_timeoff");
  const pendingTimeOff = useTimeOffRequests().data?.length ?? 0;
  // ONCALL-SCHED-01b: swap requests badge the On-Call pill the same way.
  const canOncallBuild = useCan("team.oncall_build");
  const canOncallSwap = useCan("team.oncall_swap");
  const canOncall = canOncallBuild || canOncallSwap;
  const pendingSwaps = useOncallSwapRequests("requested").data?.length ?? 0;
  return (
    // The Library is a file browser (LIB-EXPLORER-01) — it gets the room one needs.
    <div className={cn("mx-auto space-y-5 p-8", pane === "library" ? "max-w-[1760px]" : "max-w-7xl")}>
      <PageHeader
        icon={Contact}
        title="Team"
        description="Reach the field, see the week — push a message to the crew, glance the install week, keep the library current."
        className="mb-0"
      />

      <div className="flex items-center gap-1.5">
        {([
          ["messages", "Messages", Megaphone],
          ["week", "Install Week", CalendarRange],
          ["oncall", "On-Call", Moon],
          ["scoreboard", "Scoreboard", Trophy],
          ["roster", "Roster", Contact],
          ["library", "Library", FolderOpen],
        ] as const).map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setPane(k)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors",
              pane === k ? "bg-brand-700 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}>
            <Icon className="h-3.5 w-3.5" /> {label}
            {k === "week" && canApproveTimeOff && pendingTimeOff > 0 && (
              <span className="rounded-full bg-amber-500 px-1.5 py-px text-[10px] font-bold text-white">{pendingTimeOff}</span>
            )}
            {k === "oncall" && canOncall && pendingSwaps > 0 && (
              <span className="rounded-full bg-sky-600 px-1.5 py-px text-[10px] font-bold text-white">{pendingSwaps}</span>
            )}
          </button>
        ))}
      </div>

      {pane === "messages" && <MessagesPane />}
      {pane === "week" && (<><TimeOffApprovalsCard /><InstallWeekPane /></>)}
      {pane === "oncall" && <OncallPane />}
      {pane === "scoreboard" && <ScoreboardPane />}
      {pane === "roster" && <RosterPane />}
      {pane === "library" && <LibraryExplorer />}
    </div>
  );
}

// ── Install week ────────────────────────────────────────────────────────────
// The weekly PPTX (one slide per day, Installer/Time/Job#/Customer/Equip),
// on one screen and always current. Empty weekday on a crew lane = OPEN —
// that's the glance a selling tech needs to offer a date on the spot.

const isoOf = (d: Date) => d.toLocaleDateString("en-CA");
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));

function fmtLegTime(t: string | null): string {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h)) return t;
  const ap = h >= 12 ? "p" : "a";
  const h12 = ((h + 11) % 12) + 1;
  return m ? `${h12}:${String(m).padStart(2, "0")}${ap}` : `${h12}${ap}`;
}

/** ESC install notes read "8am, *Lennox Feel The Love*, Install ML296…" —
 *  pull the *promo* out as a chip, drop the leading time echo, keep the rest
 *  as the equip/scope line. */
function splitNotes(notes: string | null): { promo: string | null; rest: string } {
  if (!notes) return { promo: null, rest: "" };
  let n = notes.replace(/^\s*\d{1,2}(:\d{2})?\s*(am|pm)?\s*,\s*/i, "");
  const m = n.match(/\*([^*]+)\*/);
  const promo = m ? m[1].trim() : null;
  if (m) n = (n.slice(0, m.index) + n.slice((m.index ?? 0) + m[0].length)).replace(/^\s*,\s*/, "");
  return { promo, rest: n.replace(/\s+/g, " ").trim() };
}

/** What's being installed, from the model numbers in the notes. Lennox-speak:
 *  …UH… = furnace, KC/XC/KP/AC1x = condenser or heat pump. Full System when
 *  both sides appear. Oddballs (mini split, rooftop, garage, boiler) get
 *  named — they don't fit the morning/afternoon rhythm. */
function eqType(notes: string | null): { label: string | null; halfDay: boolean } {
  const n = notes ?? "";
  if (/mini.?split|VIR\d/i.test(n)) return { label: "Mini Split", halfDay: false };
  if (/rooftop|RTU/i.test(n)) return { label: "Rooftop", halfDay: false };
  if (/garage/i.test(n)) return { label: "Garage Unit", halfDay: false };
  if (/boiler/i.test(n)) return { label: "Boiler", halfDay: false };
  const hasFurn = /UH\d|furnace/i.test(n);
  const hasHP = /KP\d?V?-?\d|heat.?pump|\bHP\b/i.test(n);
  const hasAC = hasHP || /\b[A-Z]{2}\d{2}(KC|XC)\d?V?-?\d|AC1\d[A-Z]?\d|condenser/i.test(n) || /\b(KC|XC)\d-?\d{3}/.test(n);
  if (hasFurn && hasAC) return { label: "Full System", halfDay: false };
  if (hasFurn) return { label: "Furnace", halfDay: true };
  if (hasHP) return { label: "Heat Pump", halfDay: true };
  if (hasAC) return { label: "AC", halfDay: true };
  return { label: null, halfDay: false };
}

/** A leg is a MORNING job when it starts before 11 — the typical crew day is
 *  a morning AC/furnace plus an afternoon AC/furnace, or one full system. */
const isMorning = (t: string | null) => {
  const h = t ? parseInt(t.split(":")[0] ?? "", 10) : NaN;
  return Number.isNaN(h) ? true : h < 11;
};

type Avail = { slot: "am" | "pm" | "day"; label: string | null; overridden: boolean } | null;

/** The auto rule (coordinator override wins, handled by the caller):
 *  no legs → whole day open · one half-day job (AC/furnace/HP) → the other
 *  half is open · full systems, oddballs, or 2+ legs → day full. Errs toward
 *  NOT overpromising — the coordinator can always force a slot open. */
function autoAvail(legs: InstallLeg[], isSat: boolean): Avail {
  if (isSat) return null; // Saturdays only open by coordinator say-so
  if (legs.length === 0) return { slot: "day", label: null, overridden: false };
  if (legs.length > 1) return null;
  const t = eqType(legs[0].notes);
  if (!t.halfDay) return null;
  return isMorning(legs[0].tpromtime)
    ? { slot: "pm", label: "PM · 11-1 / 12-4", overridden: false }
    : { slot: "am", label: "AM · 8-12", overridden: false };
}

const AVAIL_SUB: Record<"am" | "pm" | "day", string> = {
  day: "All Day",
  am: "AM",
  pm: "PM",
};

export type WeekLane = {
  empno: string;
  name: string;
  kind: "crew" | "helper" | "guest" | "unassigned";
  /** Guest lanes only render these days — the rest stay blank on purpose. */
  guestDays?: Set<string>;
};

function InstallWeekPane() {
  // The visible week rides in the URL too (?wk=2026-08-24) so Back — smart
  // or browser — restores the exact week you were looking at.
  const [searchParams, setSearchParams] = useSearchParams();
  const thisMonday = mondayOf(new Date());
  const wkParam = searchParams.get("wk");
  const monday = useMemo(() => {
    if (wkParam && /^\d{4}-\d{2}-\d{2}$/.test(wkParam)) return mondayOf(new Date(wkParam + "T12:00:00"));
    return thisMonday;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wkParam]);
  const weekOffset = Math.round((monday.getTime() - thisMonday.getTime()) / (7 * 86400000));
  const gotoWeek = (m: Date | null) => {
    const next = new URLSearchParams(searchParams);
    if (m) next.set("wk", isoOf(m)); else next.delete("wk");
    setSearchParams(next, { replace: true });
  };
  const days = useMemo(() => Array.from({ length: 6 }, (_, i) => addDays(monday, i)), [monday]); // Mon..Sat
  const todayIso = isoOf(new Date());
  const { data, isLoading } = useInstallWeek(isoOf(monday));

  // Lanes (Seth 2026-09-02: the install schedule "only should be showing the
  // installers unless I manually add an employee for that specific day"):
  // the Settings-picked installers, then the day-scoped guests you added,
  // then Unassigned if any install has no tech yet. Anyone else carrying an
  // INSTAL leg this week is NOT given a lane — they're listed under the grid
  // with a one-click "add for that day" so nothing scheduled goes unseen.
  const lanes = useMemo<WeekLane[]>(() => {
    const crew: WeekLane[] = (data?.crew ?? []).map((c) => ({ empno: c.empno, name: c.name, kind: "crew" }));
    const seen = new Set(crew.map((c) => c.empno));
    const guestMap = new Map<string, WeekLane>();
    for (const g of data?.guests ?? []) {
      if (seen.has(g.empno)) continue; // already a full lane
      const l = guestMap.get(g.empno) ?? { empno: g.empno, name: g.name, kind: "guest" as const, guestDays: new Set<string>() };
      l.guestDays!.add(g.day);
      guestMap.set(g.empno, l);
    }
    const out = [...crew, ...guestMap.values()];
    if ((data?.legs ?? []).some((l) => l.window_code === "INSTAL" && !l.tech_empno)) {
      out.push({ empno: "", name: "Unassigned", kind: "unassigned" });
    }
    return out;
  }, [data]);

  // Techs with an INSTAL leg this week who have no lane (not crew, not added
  // as a guest that day) — surfaced as a footnote with an Add button.
  const offBoard = useMemo(() => {
    const byEmpno = new Map<string, { empno: string; name: string; days: Map<string, number> }>();
    for (const l of data?.legs ?? []) {
      if (l.window_code !== "INSTAL" || !l.tech_empno) continue;
      const lane = lanes.find((x) => x.empno === l.tech_empno);
      if (lane && (lane.kind !== "guest" || lane.guestDays?.has(l.appointment_date))) continue;
      const e = byEmpno.get(l.tech_empno) ?? { empno: l.tech_empno, name: l.tech_name ?? l.tech_empno, days: new Map<string, number>() };
      e.days.set(l.appointment_date, (e.days.get(l.appointment_date) ?? 0) + 1);
      byEmpno.set(l.tech_empno, e);
    }
    return [...byEmpno.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data, lanes]);

  const legsAt = (empno: string, iso: string) =>
    (data?.legs ?? [])
      .filter((l) => (l.tech_empno ?? "") === empno && l.appointment_date === iso)
      .sort((a, b) => (a.tpromtime ?? "").localeCompare(b.tpromtime ?? ""));

  // One "off" answer from BOTH sources: ESC OFF dispatches + board-added
  // Pegasus time off/meetings (tech_availability) — Seth 2026-08-21.
  const KIND_LABEL: Record<string, string> = {
    sick: "Sick", vacation: "Vacation", personal: "Personal", meeting: "Meeting",
    errand: "Errand", part_pickup: "Part Pickup", training: "Training", other: "Off",
  };
  const localDay = (isoTs: string) => {
    const d = new Date(isoTs);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const offAt = (empno: string, iso: string): { label: string; title: string | null } | null => {
    const escOff = (data?.offs ?? []).find((o) => o.tech_empno === empno && o.appointment_date === iso);
    if (escOff) return { label: "Off", title: escOff.notes ?? null };
    const ev = (data?.avail ?? []).find(
      (a) => a.empno === empno && localDay(a.start_at) <= iso && iso <= localDay(a.end_at),
    );
    if (ev) {
      return {
        label: (KIND_LABEL[ev.kind] ?? "Off") + (ev.status === "requested" ? " (requested)" : ""),
        title: ev.title ?? null,
      };
    }
    return null;
  };

  const overrideAt = (empno: string, iso: string) =>
    (data?.overrides ?? []).find((o) => o.empno === empno && o.day === iso) ?? null;

  const weekLabel = monday.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  const totalWeek = (data?.legs ?? []).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => gotoWeek(addDays(monday, -7))} title="Previous week"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-44 text-center text-sm font-bold text-slate-900">
          Week of {weekLabel}
          <span className="ml-2 text-xs font-medium text-slate-400">
            {weekOffset === 0 ? "· this week" : weekOffset === 1 ? "· next week" : ""}
          </span>
        </span>
        <button type="button" onClick={() => gotoWeek(addDays(monday, 7))} title="Next week"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
          <ChevronRight className="h-4 w-4" />
        </button>
        {weekOffset !== 0 && (
          <button type="button" onClick={() => gotoWeek(null)}
            className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200">
            Today
          </button>
        )}
        <span className="ml-auto text-xs tabular-nums text-slate-400">
          {totalWeek} install{totalWeek === 1 ? "" : "s"} this week
        </span>
      </div>

      {isLoading ? (
        <Card className="h-48 animate-pulse bg-slate-100" />
      ) : lanes.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-400">
          No install crew activity found in the ESC mirror yet — installs appear here as soon as they&apos;re scheduled.
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid" style={{ gridTemplateColumns: "7.5rem repeat(6, minmax(0, 1fr))" }}>
              {/* Header row */}
              <div className="border-b border-slate-200 bg-slate-50/60 px-3 py-2" />
              {days.map((d) => (
                <DayHeader key={isoOf(d)} d={d} today={isoOf(d) === todayIso} lanes={lanes} />
              ))}

              {/* One lane per installer */}
              {lanes.map((lane, li) => (
                <LaneRow key={lane.empno || "unassigned"} lane={lane} days={days} todayIso={todayIso}
                  legsAt={legsAt} offAt={offAt} overrideAt={overrideAt}
                  last={li === lanes.length - 1} />
              ))}
            </div>
          </div>
        </Card>
      )}

      {offBoard.length > 0 && <OffBoardNote offBoard={offBoard} />}

      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Open</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-300" /> Off / full</span>
        <span className="inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> adjust an opening or mark a day full</span>
        <span className="inline-flex items-center gap-1"><UserPlus2 className="h-3 w-3" /> add any employee for one day</span>
        <InfoTip label="How the Install Week works">Installs come live from ESC dispatches. Offer an open day, then lock it with the office. The installers themselves are set in Settings &gt; Install Week.</InfoTip>
        {data?.syncedAt && (
          <span className="ml-auto">
            Last ESC sync {new Date(data.syncedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
        )}
      </p>
    </div>
  );
}

/** Techs carrying installs this week who are not on the board (Seth
 *  2026-09-02: installers only, unless added for a specific day). One
 *  click adds them as a guest for that day. */
function OffBoardNote({ offBoard }: { offBoard: { empno: string; name: string; days: Map<string, number> }[] }) {
  const canAdjust = useCan("team.install_week");
  const addGuest = useAddInstallGuest();
  const dayLabel = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" });
  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-600">
      <span className="font-semibold text-slate-700">Also on installs this week, not on the board:</span>
      <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
        {offBoard.map((t) => (
          <li key={t.empno} className="inline-flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-slate-800">{t.name.split(" ")[0] || t.name}</span>
            {[...t.days.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([iso, n]) => (
              canAdjust ? (
                <button key={iso} type="button" disabled={addGuest.isPending}
                  onClick={() => addGuest.mutate({ day: iso, empno: t.empno })}
                  title={`Show ${t.name} on ${dayLabel(iso)} (${n} install${n === 1 ? "" : "s"})`}
                  className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-brand-700 ring-1 ring-brand-200 hover:bg-brand-50 disabled:opacity-50">
                  <UserPlus2 className="h-3 w-3" /> {dayLabel(iso)}
                </button>
              ) : (
                <span key={iso} className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">{dayLabel(iso)}</span>
              )
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The day-column header: name, date, and (for coordinators) the + that
 *  adds any tech's board to JUST this day. */
function DayHeader({ d, today, lanes }: { d: Date; today: boolean; lanes: WeekLane[] }) {
  const canAdjust = useCan("team.install_week");
  const [adding, setAdding] = useState(false);
  const { data: techs } = useAllActiveTechs();
  const addGuest = useAddInstallGuest();
  const iso = isoOf(d);

  const candidates = (techs ?? []).filter((t) =>
    !lanes.some((l) => l.empno === t.empno && (l.kind !== "guest" || l.guestDays?.has(iso))));

  return (
    <div className={cn(
      "group/day relative border-b border-l border-slate-200 px-2 py-2 text-center",
      today ? "bg-brand-50/70" : "bg-slate-50/60",
    )}>
      <div className={cn("text-[11px] font-semibold uppercase tracking-wider", today ? "text-brand-700" : "text-slate-500")}>
        {d.toLocaleDateString("en-US", { weekday: "short" })}
      </div>
      <div className={cn("text-sm font-bold tabular-nums", today ? "text-brand-800" : "text-slate-800")}>
        {d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
      </div>
      {canAdjust && (
        <button type="button" title="Show another tech's board on this day" onClick={() => setAdding(true)}
          className="absolute right-1 top-1 rounded p-0.5 text-slate-400 opacity-0 transition-opacity hover:bg-white hover:text-brand-600 group-hover/day:opacity-70">
          <UserPlus2 className="h-3.5 w-3.5" />
        </button>
      )}
      {adding && (
        <div className="absolute right-0 top-8 z-40 w-52 rounded-xl border border-hairline bg-white p-2.5 text-left shadow-e2">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800">
              Add a Tech · {d.toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" })}
              <InfoTip label="About adding a tech">Shows whatever is on their board that day. Default lanes are set in Settings &gt; Install Week.</InfoTip>
            </span>
            <button type="button" onClick={() => setAdding(false)} className="rounded p-0.5 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="max-h-52 space-y-0.5 overflow-y-auto">
            {candidates.map((t) => (
              <button key={t.empno} type="button" disabled={addGuest.isPending}
                onClick={() => addGuest.mutate({ day: iso, empno: t.empno }, { onSuccess: () => setAdding(false) })}
                className="block w-full rounded-md px-2 py-1 text-left text-xs text-slate-700 hover:bg-brand-50">
                {(t.firstname ?? "") || t.empname || t.empno}{t.lastname ? ` ${t.lastname}` : ""}
              </button>
            ))}
            {candidates.length === 0 && <p className="px-2 py-1 text-xs text-slate-400">Everyone&apos;s already shown.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function LaneRow({ lane, days, todayIso, legsAt, offAt, overrideAt, last }: {
  lane: WeekLane;
  days: Date[];
  todayIso: string;
  legsAt: (empno: string, iso: string) => InstallLeg[];
  offAt: (empno: string, iso: string) => { label: string; title: string | null } | null;
  overrideAt: (empno: string, iso: string) => DayOverride | null;
  last: boolean;
}) {
  const first = lane.name.split(" ")[0] || lane.name;
  return (
    <>
      <div className={cn("group/lane flex items-center gap-2 px-3 py-2", !last && "border-b border-slate-100")} title={lane.name}>
        <span className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
          lane.kind === "crew" ? "bg-violet-100 text-violet-700"
            : lane.kind === "unassigned" ? "bg-slate-100 text-slate-400"
            : "bg-sky-100 text-sky-700",
        )}>
          {first.slice(0, 1).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-800">{first}</span>
          {lane.kind === "guest" && <span className="block text-[10px] font-medium text-sky-600">Guest</span>}
        </span>
      </div>
      {days.map((d) => {
        const iso = isoOf(d);
        // A guest lane only shows its added day(s) — the rest stay blank.
        if (lane.kind === "guest" && !lane.guestDays?.has(iso)) {
          return (
            <div key={iso} className={cn("border-l border-slate-100 p-1.5", !last && "border-b", iso === todayIso && "bg-brand-50/40")}>
              <span className="px-1 text-[11px] text-slate-200">·</span>
            </div>
          );
        }
        const legs = legsAt(lane.empno, iso);
        const off = lane.empno ? offAt(lane.empno, iso) : null;
        return (
          <DayCell key={iso} lane={lane} iso={iso} legs={legs} off={off}
            override={lane.kind === "crew" ? overrideAt(lane.empno, iso) : null}
            isSat={d.getDay() === 6} today={iso === todayIso} last={last} />
        );
      })}
    </>
  );
}

/** One installer-day: booked legs, then (crew lanes only) the availability
 *  chip — auto rules with the coordinator's override winning — plus the
 *  pencil to adjust. Guest days get an ✕ to take the tech back off. */
function DayCell({ lane, iso, legs, off, override, isSat, today, last }: {
  lane: WeekLane;
  iso: string;
  legs: InstallLeg[];
  off: { label: string; title: string | null } | null;
  override: DayOverride | null;
  isSat: boolean;
  today: boolean;
  last: boolean;
}) {
  const canAdjust = useCan("team.install_week");
  const [editing, setEditing] = useState(false);
  const removeGuest = useRemoveInstallGuest();
  const isCrew = lane.kind === "crew";

  const avail: Avail = !isCrew || off ? null
    : override ? (override.avail === "none" ? null : { slot: override.avail, label: override.label, overridden: true })
    : autoAvail(legs, isSat);
  const markedFull = isCrew && !off && override?.avail === "none";

  return (
    <div className={cn(
      "group/cell relative flex flex-col gap-1 border-l border-slate-100 p-1.5",
      !last && "border-b",
      today && "bg-brand-50/40",
    )}>
      {legs.map((l) => <LegCard key={`${l.dispatch}-${l.counter ?? ""}`} leg={l} />)}

      {off ? (
        <span title={off.title ?? undefined}
          className="mt-0.5 self-start rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
          {off.label}
        </span>
      ) : avail ? (
        /* The OPEN slot is a real box — same visual weight as an install —
           and one click on it changes the window or closes it. */
        <button type="button" disabled={!canAdjust}
          onClick={() => canAdjust && setEditing(true)}
          title={canAdjust ? "Click to change the window or mark full" : avail.overridden ? "Set by the install coordinator" : "Auto — a typical crew day fits a morning + an afternoon job"}
          className={cn(
            "rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50/50 px-2 py-1.5 text-left transition-colors",
            canAdjust && "cursor-pointer hover:border-emerald-400 hover:bg-emerald-50",
          )}>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-400" />
            <span className="text-[13px] font-bold text-emerald-800">Open</span>
          </span>
          <span className="mt-0.5 block truncate text-[11px] font-medium text-emerald-700">
            {avail.label ?? AVAIL_SUB[avail.slot]}
          </span>
        </button>
      ) : markedFull ? (
        <button type="button" disabled={!canAdjust}
          onClick={() => canAdjust && setEditing(true)}
          title={override?.label ?? "Marked full by the install coordinator — click to change"}
          className={cn(
            "self-start rounded-full bg-slate-100 px-2 py-0.5 text-left text-[11px] font-medium text-slate-500",
            canAdjust && "cursor-pointer hover:bg-slate-200",
          )}>
          Full{override?.label ? ` · ${override.label}` : ""}
        </button>
      ) : legs.length === 0 ? (
        <span className="px-1 text-[11px] text-slate-200">{lane.kind === "guest" ? "Nothing on their board" : "—"}</span>
      ) : null}

      {/* Booked-solid days still need a way in (force a slot open). */}
      {canAdjust && isCrew && !off && !avail && !markedFull && (
        <button type="button" title="Adjust availability" onClick={() => setEditing(true)}
          className="absolute right-1 top-1 rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-white hover:text-brand-600 group-hover/cell:opacity-60">
          <Pencil className="h-3 w-3" />
        </button>
      )}
      {canAdjust && lane.kind === "guest" && (
        <button type="button" title="Remove from this day" disabled={removeGuest.isPending}
          onClick={() => removeGuest.mutate({ day: iso, empno: lane.empno })}
          className="absolute right-1 top-1 rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-white hover:text-rose-500 group-hover/cell:opacity-60">
          <X className="h-3 w-3" />
        </button>
      )}
      {editing && <AvailEditor lane={lane} iso={iso} current={override} openUp={last} onClose={() => setEditing(false)} />}
    </div>
  );
}

/** The coordinator's popover: force a slot open, relabel the window, mark
 *  the day full, or hand it back to the auto rules. */
function AvailEditor({ lane, iso, current, openUp, onClose }: {
  lane: { empno: string; name: string };
  iso: string;
  current: DayOverride | null;
  /** Bottom lane opens upward so the overflow container can't clip it. */
  openUp?: boolean;
  onClose: () => void;
}) {
  const save = useSetDayOverride();
  const [custom, setCustom] = useState("");
  const dayLabel = new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" });

  // One click = saved and closed (Seth: "with a quick click I would like to
  // change the time window"). Presets map to the ESC-style windows the sales
  // techs already speak.
  const saveNow = (avail: "auto" | "day" | "am" | "pm" | "none", label: string | null) =>
    save.mutate({ day: iso, empno: lane.empno, avail, label: label ?? "" }, { onSuccess: onClose });

  const WINDOWS: { label: string; avail: "am" | "pm" }[] = [
    { label: "8-12", avail: "am" },
    { label: "10-2", avail: "am" },
    { label: "11-1", avail: "pm" },
    { label: "12-4", avail: "pm" },
    { label: "3-7", avail: "pm" },
  ];

  return (
    <div className={cn("absolute right-0 z-40 w-60 rounded-xl border border-hairline bg-white p-3 shadow-e2", openUp ? "bottom-6" : "top-6")}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold text-slate-800">{lane.name.split(" ")[0]} · {dayLabel}</span>
        <button type="button" onClick={onClose} className="rounded p-0.5 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
      </div>

      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Open a Window</p>
      <div className="flex flex-wrap gap-1">
        {WINDOWS.map((w) => (
          <button key={w.label} type="button" disabled={save.isPending}
            onClick={() => saveNow(w.avail, w.label)}
            className={cn("rounded-md border px-2 py-1 text-xs font-semibold transition-colors",
              current?.label === w.label
                ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                : "border-hairline text-slate-600 hover:border-emerald-300 hover:bg-emerald-50/60")}>
            {w.label}
          </button>
        ))}
        <button type="button" disabled={save.isPending} onClick={() => saveNow("day", null)}
          className={cn("rounded-md border px-2 py-1 text-xs font-semibold transition-colors",
            current?.avail === "day"
              ? "border-emerald-400 bg-emerald-50 text-emerald-800"
              : "border-hairline text-slate-600 hover:border-emerald-300 hover:bg-emerald-50/60")}>
          All Day
        </button>
      </div>
      <form className="mt-1.5 flex items-center gap-1"
        onSubmit={(e) => { e.preventDefault(); if (custom.trim()) saveNow("pm", custom.trim()); }}>
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder='Custom, e.g. "1-5" or "after 2"'
          className="min-w-0 flex-1 rounded-md border border-hairline px-2 py-1 text-xs focus:border-brand-500 focus:outline-none" />
        <button type="submit" disabled={save.isPending || !custom.trim()}
          className="rounded-md bg-brand-700 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-40">
          Set
        </button>
      </form>

      <div className="mt-2.5 flex items-center gap-1.5 border-t border-slate-100 pt-2">
        <button type="button" disabled={save.isPending} onClick={() => saveNow("none", null)}
          className={cn("flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors",
            current?.avail === "none" ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>
          Mark Full
        </button>
        <button type="button" disabled={save.isPending} onClick={() => saveNow("auto", null)}
          title="Back to the auto rule — openings computed from what's booked"
          className="flex-1 rounded-md bg-slate-50 px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100">
          Auto
        </button>
        {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      </div>
      {save.isError && <p className="mt-1.5 text-[11px] font-medium text-rose-600">Couldn&apos;t save — try again.</p>}
    </div>
  );
}

function LegCard({ leg }: { leg: InstallLeg }) {
  // Records where you came from, so the job's Back button (and browser back)
  // return HERE — same pane, same week — not to the board.
  const navigateWithBack = useNavigateWithBack();
  const name = displayCustomerName(null, leg.customer_fullname);
  const { promo, rest } = splitNotes(leg.notes);
  const type = eqType(leg.notes);
  const done = ["done", "complete"].includes((leg.status ?? "").toLowerCase());
  const inner = (
    <div title={leg.notes ?? undefined} className={cn(
      "rounded-lg border px-2 py-1.5 text-left transition-colors",
      done ? "border-slate-100 bg-slate-50/60 opacity-70" : "border-brand-100 bg-white shadow-e1 hover:border-brand-300",
    )}>
      <div className="flex items-baseline gap-1.5">
        {leg.tpromtime && <span className="text-[11px] font-bold tabular-nums text-brand-700">{fmtLegTime(leg.tpromtime)}</span>}
        <span className="min-w-0 truncate text-[13px] font-semibold text-slate-800">{name}</span>
        {done && <CheckCheck className="ml-auto h-3 w-3 shrink-0 text-emerald-500" />}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {leg.city && <span className="truncate text-[11px] text-slate-500">{leg.city}</span>}
        {leg.window_code && leg.window_code !== "INSTAL" && (
          <span className="rounded bg-slate-100 px-1 py-px text-[10px] font-semibold text-slate-500">{leg.window_code}</span>
        )}
        {type.label && (
          <span className="rounded bg-brand-50 px-1 py-px text-[10px] font-semibold text-brand-700">{type.label}</span>
        )}
        {promo && (
          <span className="max-w-full truncate rounded bg-amber-50 px-1 py-px text-[10px] font-semibold text-amber-700">{promo}</span>
        )}
      </div>
      {rest && <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-400">{rest}</div>}
    </div>
  );
  // Straight to the JOB (the dispatch), not the customer's 360 — Seth's call.
  return (
    <button type="button" onClick={() => navigateWithBack(`/dispatch/${leg.dispatch}`)} className="block w-full text-left">
      {inner}
    </button>
  );
}

// ── Time-off approvals (TIMEOFF-01) ─────────────────────────────────────────
// Requests from the field app land here (and ping approvers in chat). Sits on
// top of the Install Week pane — approving time off IS a schedule decision.
function TimeOffApprovalsCard() {
  const canApprove = useCan("team.approve_timeoff");
  const { data: reqs } = useTimeOffRequests();
  const { data: techs } = useAllActiveTechs();
  const decide = useDecideTimeOff();
  const [denying, setDenying] = useState<string | null>(null);
  const [note, setNote] = useState("");
  // ONCALL-SCHED-01b: every waiting request checked against the PUBLISHED
  // on-call calendar — one query for the whole span, matched per row.
  const spanFrom = (reqs ?? []).reduce<string | null>((m, r) => { const d = r.start_at.slice(0, 10); return !m || d < m ? d : m; }, null);
  const spanTo = (reqs ?? []).reduce<string | null>((m, r) => { const d = new Date(new Date(r.end_at).getTime() - 60000).toLocaleDateString("en-CA"); return !m || d > m ? d : m; }, null);
  const oncallDays = useOncallDaysPublished(spanFrom ?? oncallISO(new Date()), spanTo ?? oncallISO(new Date()), !!spanFrom && !!spanTo).data ?? [];
  const oncallFor = (r: TimeOffRequest) => myOncallDaysIn(oncallDays, r.empno, new Date(r.start_at).toLocaleDateString("en-CA"), new Date(new Date(r.end_at).getTime() - 60000).toLocaleDateString("en-CA"));
  if (!canApprove || !reqs || reqs.length === 0) return null;

  const nameOf = (empno: string) => {
    const t = (techs ?? []).find((x) => x.empno === empno);
    return (t?.firstname && t?.lastname ? `${t.firstname} ${t.lastname}` : t?.firstname) ?? t?.empname ?? empno;
  };
  const spanOf = (r: TimeOffRequest) => {
    const s = new Date(r.start_at);
    const e = new Date(new Date(r.end_at).getTime() - (r.all_day ? 60000 : 0));
    const f = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    if (r.all_day) return f(s) === f(e) ? f(s) : `${f(s)} – ${f(e)}`;
    const t = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${f(s)}, ${t(s)} – ${t(e)}`;
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40 p-4">
      <div className="flex items-center gap-2">
        <Palmtree className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-bold text-slate-900">Time Off Requests</span>
        <InfoTip label="What happens when you decide">The tech hears back in chat the moment you approve or deny.</InfoTip>
        <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{reqs.length}</span>
      </div>
      <ul className="mt-2.5 space-y-2">
        {reqs.map((r) => (
          <li key={r.id} className="rounded-lg border border-amber-200/70 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2.5">
              <TechAvatar empno={r.empno} name={nameOf(r.empno)} size="sm" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800">
                  {nameOf(r.empno)}
                  <span className="ml-1.5 font-normal text-slate-500">· {AVAIL_KIND_LABEL[r.kind as AvailKind] ?? r.kind}</span>
                </span>
                <span className="block text-xs text-slate-500">
                  {spanOf(r)}{r.title ? <span className="text-slate-400"> — &ldquo;{r.title}&rdquo;</span> : null}
                </span>
                {oncallFor(r).length > 0 && (
                  <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px]">
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-px font-semibold text-red-800 ring-1 ring-red-200/70">
                      On call {oncallFor(r).map((d) => `${fmtOncallDay(d.day)} (${d.role})`).join(", ")} — approving leaves {oncallFor(r).length === 1 ? "it" : "them"} uncovered
                    </span>
                    <Link to={`/team?pane=oncall&day=${oncallFor(r)[0].day}`} className="font-semibold text-brand-700 hover:underline">Swap in…</Link>
                  </span>
                )}
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                <button type="button" disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: r.id, decision: "approved" })}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50">
                  <Check className="h-3.5 w-3.5" /> Approve
                </button>
                <button type="button" disabled={decide.isPending}
                  onClick={() => { setDenying(denying === r.id ? null : r.id); setNote(""); }}
                  className="rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-600 ring-1 ring-rose-200 hover:bg-rose-50">
                  Deny…
                </button>
              </span>
            </div>
            {denying === r.id && (
              <div className="mt-2 flex items-center gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} autoFocus
                  placeholder="Why? (goes to the tech — optional)"
                  className="flex-1 rounded-md border border-hairline px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none" />
                <button type="button" disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: r.id, decision: "denied", note }, { onSuccess: () => setDenying(null) })}
                  className="rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                  Confirm deny
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ── Messages ────────────────────────────────────────────────────────────────
// The tab's primary tool: announcements, "please contact this customer"
// (the customer rides along — name, address, phone), and plain messages.
// ANY of them can carry an attachment — a one-time upload or a Library doc
// — plus an optional Good Through date (financing, incentives, promos).

const KIND_META: Record<MessageKind, { label: string; icon: typeof Megaphone; chip: string }> = {
  note: { label: "Announcement", icon: Megaphone, chip: "bg-slate-100 text-slate-500" },
  contact_customer: { label: "Contact Customer", icon: PhoneCall, chip: "bg-sky-50 text-sky-700" },
  message: { label: "Message", icon: MessageSquare, chip: "bg-indigo-50 text-indigo-700" },
};

const KIND_PLACEHOLDER: Record<MessageKind, string> = {
  note: "e.g. Shop meeting Friday 7am — donuts on Seth. New F200 filters are on shelf B4.",
  contact_customer: "e.g. She's called twice about the noisy condenser — please call her before lunch.",
  message: "e.g. Lennox $600 rebate is live through 9/30 — price book is updated. Push ML296 quotes this week.",
};

function MessagesPane() {
  const { data: bulletins } = useBulletins();
  const { data: docs } = useLibraryDocs();
  const { data: techs } = useAllActiveTechs();
  const send = useSendBulletin();
  const [kind, setKind] = useState<MessageKind>("note");
  const [body, setBody] = useState("");
  const [cust, setCust] = useState<PickedCustomer | null>(null);
  // Attachment: EITHER a Library doc (docId) OR a fresh file. A fresh file is
  // one-time by default; "Also add to the Library" files it too (any folder,
  // new folders welcome; same-name = replace, the no-duplicates path).
  const [docId, setDocId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [addToLib, setAddToLib] = useState(false);
  const [folder, setFolder] = useState<string>(LIBRARY_FOLDERS[0]);
  const [newFolder, setNewFolder] = useState("");
  const [expires, setExpires] = useState("");
  const attachRef = useRef<HTMLInputElement>(null);

  const folderChoices = useMemo(() => {
    const s = new Set<string>(LIBRARY_FOLDERS);
    for (const d of docs ?? []) if (d.folder) s.add(d.folder);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [docs]);
  const effFolder = folder === "__new__" ? newFolder : folder;
  const replaceTarget = file && addToLib
    ? (docs ?? []).find((d) => d.filename.toLowerCase() === file.name.toLowerCase()) ?? null
    : null;
  // Audience: everyone, or a hand-picked set of techs (Seth 2026-08-20:
  // "individually send messages to techs"). Same rail, targeted delivery.
  const [toAll, setToAll] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const techName = useMemo(() => new Map((techs ?? []).map((t) => [t.empno, (t.firstname ?? "") || (t.empname ?? "") || t.empno])), [techs]);

  const togglePick = (empno: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(empno)) n.delete(empno); else n.add(empno);
    return n;
  });

  const pickCustomer = (c: PickedCustomer) => {
    setCust(c);
    if (!body.trim()) setBody(`Please call ${c.name}${c.phone ? ` (${c.phone})` : ""} — `);
  };

  const canSend = body.trim().length > 0 && (toAll || picked.size > 0)
    && (kind !== "contact_customer" || cust != null)
    && !(file && addToLib && folder === "__new__" && !newFolder.trim());
  const doSend = () => {
    const doc = !file && docId ? (docs ?? []).find((d) => d.id === docId) : undefined;
    send.mutate(
      {
        body: body.trim(),
        audience: toAll ? "techs" : [...picked],
        kind,
        custno: kind === "contact_customer" ? cust?.custno ?? null : null,
        customerLabel: kind === "contact_customer" && cust
          ? [cust.name, [cust.address, cust.city].filter(Boolean).join(", "), cust.phone].filter(Boolean).join(" · ")
          : null,
        attachLibraryDocId: doc?.id ?? null,
        attachLibraryDocLabel: doc?.filename ?? null,
        file,
        addToLibrary: file && addToLib ? { folder: effFolder, replaceDoc: replaceTarget } : null,
        expiresOn: expires || null,
      },
      { onSuccess: () => {
        setBody(""); setCust(null); setDocId(""); setFile(null); setAddToLib(false);
        setFolder(LIBRARY_FOLDERS[0]); setNewFolder(""); setExpires(""); setPicked(new Set()); setToAll(true);
      } },
    );
  };
  const audienceLabel = (a: string) => a === "techs" ? "Everyone" : a.split(",").map((e) => techName.get(e.trim()) ?? e.trim()).join(", ");

  return (
    <div className="space-y-4">
      <Card className="p-4">
        {/* What kind of message is this? */}
        <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
          {(Object.keys(KIND_META) as MessageKind[]).map((k) => {
            const M = KIND_META[k];
            return (
              <button key={k} type="button" onClick={() => setKind(k)}
                className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                  kind === k ? "bg-brand-700 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>
                <M.icon className="h-3.5 w-3.5" /> {M.label}
              </button>
            );
          })}
          <InfoTip label="About messages">Post an announcement, hand a tech a customer to call, or share a new incentive. A Contact Customer message carries the customer&apos;s name, address and phone, so the tech can call in one tap. Delivery and seen-by receipts turn on with the field app.</InfoTip>
        </div>

        {/* Contact-customer: the customer rides along. */}
        {kind === "contact_customer" && (
          <div className="mb-2.5">
            {cust ? (
              <CustomerPicker selected={cust} onClear={() => setCust(null)} onSelect={pickCustomer} />
            ) : (
              <CustomerPicker onSelect={pickCustomer} placeholder="Which customer should they contact? Search name, address, phone…" autoFocus />
            )}
          </div>
        )}

        {/* Attachment + expiry — available on EVERY kind of message. */}
        <div className="mb-2.5 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            {file ? (
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50/60 px-2.5 py-1.5 text-xs font-medium text-indigo-800">
                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{file.name}</span>
                <button type="button" onClick={() => { setFile(null); setAddToLib(false); }} title="Remove attachment"
                  className="shrink-0 rounded p-0.5 text-indigo-400 hover:bg-white hover:text-indigo-700">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <>
                <button type="button" onClick={() => attachRef.current?.click()}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-200">
                  <Paperclip className="h-3.5 w-3.5" /> Attach a File
                </button>
                <input ref={attachRef} type="file" className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setFile(f); setDocId(""); }
                  e.target.value = "";
                }} />
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  or from the Library:
                  <select value={docId} onChange={(e) => setDocId(e.target.value)}
                    className="max-w-52 rounded-md border border-hairline px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none">
                    <option value="">No Attachment</option>
                    {(docs ?? []).map((d) => <option key={d.id} value={d.id}>{d.filename}</option>)}
                  </select>
                </span>
              </>
            )}
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              Good Through
              <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)}
                className="rounded-md border border-hairline px-2 py-1 text-xs focus:border-brand-500 focus:outline-none" />
            </label>
          </div>

          {file && (
            <div className="flex flex-wrap items-center gap-2 pl-1">
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={addToLib} onChange={(e) => setAddToLib(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500/40" />
                Also add to the Library
              </label>
              {!addToLib && <span className="text-[11px] text-slate-400">One-time — rides on this message only.</span>}
              {addToLib && (
                <>
                  <select value={folder} onChange={(e) => setFolder(e.target.value)}
                    className="rounded-md border border-hairline px-2 py-1 text-xs focus:border-brand-500 focus:outline-none">
                    {folderChoices.map((f) => <option key={f} value={f}>{f}</option>)}
                    <option value="__new__">New Folder…</option>
                  </select>
                  {folder === "__new__" && (
                    <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="Folder name" autoFocus
                      className="w-36 rounded-md border border-hairline px-2 py-1 text-xs focus:border-brand-500 focus:outline-none" />
                  )}
                </>
              )}
              {replaceTarget && (
                <span className="rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800">
                  &ldquo;{replaceTarget.filename}&rdquo; is already in the Library (v{replaceTarget.version}) — sending will replace it (becomes v{replaceTarget.version + 1}, old version stays in history).
                </span>
              )}
            </div>
          )}
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">To:</span>
          <button type="button" onClick={() => setToAll(true)}
            className={cn("rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
              toAll ? "bg-brand-700 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>
            Everyone
          </button>
          <button type="button" onClick={() => setToAll(false)}
            className={cn("rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
              !toAll ? "bg-brand-700 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>
            Pick Techs{!toAll && picked.size > 0 ? ` · ${picked.size}` : ""}
          </button>
          {!toAll && (techs ?? []).map((t) => (
            <button key={t.empno} type="button" onClick={() => togglePick(t.empno)}
              className={cn("rounded-full px-2.5 py-1 text-xs transition-colors",
                picked.has(t.empno) ? "bg-violet-100 font-semibold text-violet-800" : "bg-slate-50 text-slate-500 hover:bg-slate-100")}>
              {(t.firstname ?? "") || t.empname || t.empno}
            </button>
          ))}
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
          placeholder={KIND_PLACEHOLDER[kind]}
          className="w-full rounded-md border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        {send.isError && <p className="mt-1 text-xs font-medium text-rose-600">Couldn&apos;t send — try again.</p>}
        <div className="mt-2 flex justify-end">
          <button type="button" disabled={send.isPending || !canSend}
            onClick={doSend}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 disabled:opacity-50">
            {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {toAll ? "Send to All Techs" : `Send to ${picked.size || "…"} Tech${picked.size === 1 ? "" : "s"}`}
          </button>
        </div>
      </Card>

      <ul className="space-y-2.5">
        {(bulletins ?? []).map((b) => <MessageCard key={b.id} b={b} docs={docs} audienceLabel={audienceLabel} />)}
        {(bulletins ?? []).length === 0 && (
          <Card className="p-6 text-center text-sm text-slate-400">No messages yet — the history starts with your first send.</Card>
        )}
      </ul>
    </div>
  );
}

function MessageCard({ b, docs, audienceLabel }: {
  b: Bulletin;
  docs: LibraryDoc[] | undefined;
  audienceLabel: (a: string) => string;
}) {
  const meta = KIND_META[(b.kind as MessageKind) ?? "note"] ?? KIND_META.note;
  const expired = b.expires_on ? b.expires_on < new Date().toLocaleDateString("en-CA") : false;
  const openAttachment = async () => {
    // Library doc (current version) or the message's own one-time file.
    const path = b.doc_id
      ? (docs ?? []).find((x) => x.id === b.doc_id)?.storage_path ?? null
      : b.attachment_path;
    if (!path) return;
    const url = await libraryDocUrl(path);
    if (url) window.open(url, "_blank", "noopener");
  };
  const attachLabel = b.doc_label ?? b.attachment_name;
  return (
    <li>
      <Card className={cn("p-4", expired && "opacity-60")}>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{b.body}</p>

        {b.kind === "contact_customer" && b.custno && (
          <Link to={`/customers/${b.custno}`}
            className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-lg border border-sky-100 bg-sky-50/60 px-2.5 py-1.5 text-xs font-medium text-sky-800 transition-colors hover:border-sky-300">
            <PhoneCall className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{b.customer_label ?? b.custno}</span>
          </Link>
        )}

        {(attachLabel || b.expires_on) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {attachLabel && (
              <button type="button" onClick={openAttachment}
                className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50/60 px-2.5 py-1.5 text-xs font-medium text-indigo-800 transition-colors hover:border-indigo-300">
                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{attachLabel}</span>
                {!b.doc_id && <span className="shrink-0 text-[10px] font-normal text-indigo-400">one-time</span>}
              </button>
            )}
            {b.expires_on && (
              <span className={cn("text-[11px] font-medium", expired ? "text-slate-400 line-through" : "text-amber-700")}>
                Good Through {new Date(b.expires_on + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        )}

        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
          <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium", meta.chip)}>
            <meta.icon className="h-3 w-3" /> {meta.label}
          </span>
          <span className={cn("rounded-full px-2 py-0.5 font-medium",
            b.audience === "techs" ? "bg-slate-100 text-slate-500" : "bg-violet-50 text-violet-700")}>
            {audienceLabel(b.audience)}
          </span>
          <span>{b.created_by ?? "office"}</span>
          <span>·</span>
          <span>{new Date(b.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-400" title="Delivery receipts start with the field app">
            <CheckCheck className="h-3 w-3" /> receipts with the app
          </span>
        </div>
      </Card>
    </li>
  );
}
